import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  GridItem,
  Text,
  Image,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Badge,
  Flex,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Textarea,
  useDisclosure,
  useToast,
  Switch,
  Select
} from '@chakra-ui/react';
import AnimatedCard from './AnimatedCard';

import { EditIcon, AttachmentIcon, DeleteIcon, LockIcon, CheckCircleIcon } from '@chakra-ui/icons';
import SignatureCanvas from 'react-signature-canvas';
import { uploadSignatureImage, lockUserItemPreference, deleteUserEquipment, uploadUserItemPreferenceMaterial } from '../services/api';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useNavigate, useLocation } from 'react-router-dom';
import rank1Image from '/images/rank1.png';
import rank2Image from '/images/rank2.png';
import rank3Image from '/images/rank3.png';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
dayjs.extend(utc);
dayjs.extend(timezone);

function UserEquipmentModal({ isOpen, onClose, onRegister, initialData, config, targetUserId, isTsubo }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFunctionEnabled } = useAuth();
  const generateInitialForm = (fields) => {
    let initial = {};
    if (fields) {
      Object.entries(fields).forEach(([name, field]) => {
        if (field.type === 'number') {
          initial[field.name] = '';
        } else if (field.type === 'boolean') {
          initial[field.name] = field.default || false;
        } else {
          initial[field.name] = '';
        }
      });
    }
    // 外部リンクフィールドと画像配列を初期化
    initial.external_link_url = '';
    initial.external_link_name = '';
    initial.images = []; // 画像配列を初期化
    return initial;
  };

  const formFields = config?.fields || {};
  const [form, setForm] = useState(() => generateInitialForm(formFields));
  const [rooms, setRooms] = useState(initialData?.rooms || []);
  const [imageFiles, setImageFiles] = useState([]);
  const [imageDescriptions, setImageDescriptions] = useState([]);
  const fileInputRef = useRef();

  // モーダルが開いたときにフォームを初期化/リセット
  useEffect(() => {
    if (isOpen) {
      setForm(generateInitialForm(formFields));
      setRooms(initialData?.rooms || []);
      setImageFiles([]);
      setImageDescriptions([]);
    }
  }, [isOpen, formFields, initialData]);

  // FloorPlannerから戻ってきた場合roomsを反映
  useEffect(() => {
    if (isOpen && location.state?.rooms) {
      setRooms(location.state.rooms);
    }
  }, [isOpen, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleNumberChange = (name, value) => {
    if ( value === '' ) {
      setForm(f => f.drop(name));
    } else {
      setForm(f => ({ ...f, [name]: Number(value) }));
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 5 - imageFiles.length);
    setImageFiles(prev => [...prev, ...files]);
    setImageDescriptions(prev => [...prev, ...Array(files.length).fill('')]);
    setForm(f => ({ ...f, images: [...f.images, ...files] }));
    fileInputRef.current.value = '';
  };

  const handleImageRemove = (idx) => {
    setImageFiles(prev => prev.filter((_, i) => i !== idx));
    setImageDescriptions(prev => prev.filter((_, i) => i !== idx));
    setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const handleImageDescriptionChange = (idx, description) => {
    setImageDescriptions(prev => {
      const newDescriptions = [...prev];
      newDescriptions[idx] = description;
      return newDescriptions;
    });
  };

  const handleEditFloorplan = () => {
    const url = (targetUserId && targetUserId !== auth.currentUser?.uid) ? `/admin/user/${targetUserId}/planner` : '/planner';
    navigate(url, { state: { floorRooms: rooms, targetUserId: targetUserId, mode: 'create' } });
  };

  const handleRegister = () => {
    const data = { ...form };
    for ( const field of config.fields ) {
      if ( field.type === 'number' && data[field.name] === '' ) {
        delete data[field.name];
      }
    }
    if (config.key === 'floorplan') {
      data.rooms = rooms;
    }
    
    // 画像とdescriptionのペアを設定
    const imageData = imageFiles.map((file, idx) => ({
      file: file,
      description: imageDescriptions[idx] || ''
    }));
    data.imageData = imageData;
    
    onRegister(data);
  };

  // 動的にフォームフィールドをレンダリングする関数
  const renderFormField = (fieldName, field) => {
    if (field.name === 'recommended' || field.name === 'images' || field.name === 'external_link_url' || field.name === 'external_link_name' || field.name === 'default_quantity') {
      return null;
    }
    if (config.key === 'floorplan' && field.unit === 'm2') {
      return (
        <FormControl key={field.name} isRequired={field.required}>
          <FormLabel>{field.label}{isTsubo ? '（坪）' : '（m²）'}</FormLabel>
          <NumberInput
            min={0}
            value={form[field.name] ?? ''}
            onChange={(valueString) => handleNumberChange(field.name, valueString)}
          >
            <NumberInputField name={field.name} />
          </NumberInput>
        </FormControl>
      );
    }
    switch (field.type) {
      case 'number':
        return (
          <FormControl key={field.name} isRequired={field.required}>
            <FormLabel>{field.label}{field.unit ? ` (${field.unit})` : ''}</FormLabel>
            <NumberInput
              min={0}
              value={form[field.name] ?? ''}
              onChange={(valueString) => handleNumberChange(field.name, valueString)}
            >
              <NumberInputField name={field.name} />
            </NumberInput>
          </FormControl>
        );
      case 'boolean':
        return (
          <FormControl key={field.name} isRequired={field.required}>
            <FormLabel>{field.label}</FormLabel>
            <Switch
              isChecked={form[field.name] ?? field.default ?? false}
              onChange={(e) => setForm(f => ({ ...f, [field.name]: e.target.checked }))}
            />
          </FormControl>
        );
      default:
        return (
          <FormControl key={field.name} isRequired={field.required}>
            <FormLabel>{field.label}</FormLabel>
            <Textarea
              name={field.name}
              value={form[field.name] ?? ''}
              onChange={handleChange}
              placeholder={field.placeholder || ''}
              type={field.inputType || 'text'}
            />
          </FormControl>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>自分で{config.key==='floorplan' ? '間取り' : '設備'}を登録</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            {Object.entries(formFields).map(([name, field]) => renderFormField(name, field))}
           <FormControl>
              <FormLabel>画像（最大5枚）</FormLabel>
              <Input type="file" accept="image/*" multiple ref={fileInputRef} onChange={handleImageChange} isDisabled={imageFiles.length >= 5} />
              <VStack mt={2} spacing={3} align="stretch">
                {imageFiles.map((file, idx) => (
                  <Box key={idx} p={3} border="1px solid" borderColor="gray.200" borderRadius="md">
                    <HStack spacing={3} align="start">
                      <Box position="relative">
                        <Image 
                          src={URL.createObjectURL(file)} 
                          width="120px" 
                          height="120px" 
                          objectFit="cover" 
                          borderRadius="md" 
                        />
                        <IconButton 
                          icon={<DeleteIcon />} 
                          size="sm" 
                          colorScheme="red" 
                          position="absolute" 
                          top={1} 
                          right={1} 
                          aria-label="画像削除" 
                          onClick={() => handleImageRemove(idx)} 
                        />
                      </Box>
                      <VStack align="start" flex={1}>
                        <Text fontSize="sm" fontWeight="bold">画像 {idx + 1}</Text>
                        <Text fontSize="xs" color="gray.500">画像の説明（オプション）</Text>
                        <Input
                          size="sm"
                          placeholder="説明を入力"
                          value={imageDescriptions[idx] || ''}
                          onChange={(e) => handleImageDescriptionChange(idx, e.target.value)}
                        />
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </FormControl>
            
            {/* 外部リンクフィールド */}
            <FormControl>
              <FormLabel>外部リンクURL</FormLabel>
              <Input
                name="external_link_url"
                value={form.external_link_url || ''}
                onChange={handleChange}
                placeholder="https://example.com"
                type="url"
              />
            </FormControl>
            <FormControl>
              <FormLabel>外部リンク表示名</FormLabel>
              <Input
                name="external_link_name"
                value={form.external_link_name || ''}
                onChange={handleChange}
                placeholder="詳細を見る"
                type="text"
              />
            </FormControl>
            
            {config.key === 'floorplan' && isFunctionEnabled('floor_plan') && (
              <Button bg="black" color="white" onClick={handleEditFloorplan} mb={2}>間取りを編集</Button>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            bg="black"
            color="white"
            _hover={{ bg: 'gray.700' }}
            _active={{ bg: 'gray.800' }}
            mr={3}
            onClick={handleRegister}
          >
            登録
          </Button>
          <Button variant="outline" colorScheme="gray" onClick={onClose}>
            キャンセル
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function SignaturePadModal({ isOpen, onClose, onSave }) {
  const sigPadRef = useRef();
  const [error, setError] = useState('');

  const handleClear = () => {
    sigPadRef.current.clear();
    setError('');
  };
  const handleSave = () => {
    if (sigPadRef.current.isEmpty()) {
      setError('サインを入力してください');
      return;
    }
    sigPadRef.current.getCanvas().toBlob(blob => {
      onSave(blob);
    });
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>サインしてロック</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <SignatureCanvas
              ref={sigPadRef}
              penColor="black"
              backgroundColor="#fff"
              canvasProps={{ width: 400, height: 200, style: { border: '1px solid #ccc', borderRadius: 8 } }}
            />
            {error && <Text color="black">{error}</Text>}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClear} mr={3}>クリア</Button>
          <Button bg="black" color="white" onClick={handleSave}>サインしてロック</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function MemoModal({ isOpen, onClose, memo, onSave, isLocked, preferenceId, materialFiles = [], onUploadMaterial, onReloadMaterialFiles, onPreferenceChange, itemType, itemId, onImageClick }) {
  const [value, setValue] = useState(memo || '');
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState(materialFiles);
  useEffect(() => {
    if (isOpen) {
      setValue(memo || '');
      setFiles(materialFiles || []);
    }
  }, [isOpen, materialFiles]);

  // ファイルアップロード処理
  const handleFileChange = async (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    setUploading(true);
    try {
      await onUploadMaterial(preferenceId, e.target.files[0]);
      if (onReloadMaterialFiles) await onReloadMaterialFiles();
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // ファイル削除処理
  const handleFileDelete = async (idx) => {
    if (isLocked) return;
    if (!window.confirm('本当にこの資料を削除してもよいですか？')) return;
    const newFiles = files.filter((_, i) => i !== idx);
    setFiles(newFiles);

    // 即時API反映
    if (preferenceId && onPreferenceChange) {
      await onPreferenceChange(itemId, itemType, { material_files: newFiles });
      if (onReloadMaterialFiles) await onReloadMaterialFiles();
    }
  };

  // ファイルの種類判定
  const isImage = (url) => /\.(jpg|jpeg|png|gif|bmp)(\?.*)?$/i.test(url);
  const isPdf = (url) => /\.pdf(\?.*)?$/i.test(url);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>メモ・資料添付</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Textarea
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="この設備に関するメモを入力..."
            isReadOnly={isLocked}
            minH="120px"
          />
          <Box mt={4}>
            <Text fontWeight="bold" mb={2}>資料ファイル添付</Text>
            <Input type="file" accept="image/*,.pdf" onChange={handleFileChange} isDisabled={isLocked || uploading} mb={2} />
            <VStack align="start" spacing={2} mt={2} maxH="1000px" overflowY="auto">
              {files && files.length > 0 ? files.map((url, idx) => (
                <Box key={idx} borderWidth="1px" borderRadius="md" p={2} w="100%" bg="gray.50" position="relative">
                  {isImage(url) ? (
                    <Box>
                      <Text fontSize="sm" color="gray.600">{`資料${idx+1}`}</Text>
                      <Image 
                        src={url} 
                        alt={`資料${idx+1}`} 
                        maxWidth="100%" 
                        maxHeight="300px" 
                        objectFit="cover" 
                        display="inline-block" 
                        mr={2} 
                        cursor="pointer"
                        onClick={() => {
                          if (onImageClick) {
                            onImageClick(url, `資料${idx+1}`);
                          }
                        }}
                        title="クリックして拡大表示"
                      />
                    </Box>
                  ) : isPdf(url) ? (
                    <Box>
                      <Text fontSize="sm" color="gray.600">{`資料${idx+1}`}</Text>
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" colorScheme="teal" variant="outline">PDFを開く/ダウンロード</Button>
                      </a>
                    </Box>
                  ) : (
                    <Box>
                      <a href={url} target="_blank" rel="noopener noreferrer">ファイルを開く</a>
                    </Box>
                  )}
                  {/* 削除ボタン */}
                  {!isLocked && (
                    <IconButton
                      icon={<DeleteIcon />}
                      size="xs"
                      colorScheme="red"
                      aria-label="添付ファイル削除"
                      position="absolute"
                      top={1}
                      right={1}
                      onClick={() => handleFileDelete(idx)}
                    />
                  )}
                </Box>
              )) : (
                <Text color="gray.400" fontSize="sm">添付ファイルはありません</Text>
              )}
            </VStack>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} mr={3}>閉じる</Button>
          <Button bg="black" color="white" onClick={() => onSave(value, files)} isDisabled={isLocked}>保存</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function EquipmentList({
  items,
  config,
  onPreferenceChange,
  onItemSelect,
  isFixedList = false,
  onReloadPreferences,
  onReloadMaterialFiles,
  user_item_preferences,
  onReloadUserEquipments,
  targetUserId,
  isTsubo = false
}) {

  const toast = useToast();
  const { isFunctionEnabled } = useAuth();

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');
  const fixedBg = useColorModeValue('gray.50', 'gray.900');
  const tileBg = useColorModeValue('white', 'gray.800');
  const tileBorder = useColorModeValue('gray.200', 'gray.600');

  const isAdmin = targetUserId != auth.currentUser?.uid;

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '価格未定';
    return (
      <>
        <Text as="span" fontSize="lg" fontWeight="bold">¥{price.toLocaleString()}</Text>
        <Text as="span" fontSize="xs" color="gray.600"> (税抜)</Text>
      </>
    );
  };

  // 画像URLの取得
  const getImageUrl = (item) => {
    // 新しい形式（images配列）をチェック
    if (item.images && item.images.length > 0) {
      const firstImage = item.images[0];
      if (typeof firstImage === 'string') {
        return firstImage;
      }
      return firstImage.url || firstImage;
    }
    // 古い形式（個別のURL）をチェック
    if (item.image1_sas_url) {
      return item.image1_sas_url;
    }
    return null;
  };



  const [lockTarget, setLockTarget] = useState(null); // {item, idx}
  const [isSigModalOpen, setSigModalOpen] = useState(false);
  const [loadingLock, setLoadingLock] = useState(false);
  
  // 拡大画像モーダル用のstate
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageAlt, setSelectedImageAlt] = useState('');
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // サインパッドモーダルを開く
  const openLockModal = (item) => {
    setLockTarget(item);
    setSigModalOpen(true);
  };
  const closeLockModal = () => {
    setLockTarget(null);
    setSigModalOpen(false);
  };

  // サイン画像アップロード＆ロックAPI呼び出し
  const handleLock = async (signatureBlob) => {
    if (!lockTarget) return;
    setLoadingLock(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('未ログインです');
      // サイン画像アップロード
      const uploadRes = await uploadSignatureImage(user.uid, lockTarget.item_type, signatureBlob);
      const signatureUrl = uploadRes.url;
      // ロック情報をUserItemPreferenceにUPSERT
      const now = new Date().toISOString();
      await lockUserItemPreference({
        item_type: lockTarget.item_type,
        item_id: lockTarget.id,
        is_liked: lockTarget.is_liked,
        is_fixed: lockTarget.is_fixed,
        locked_at: now,
        signature_url: signatureUrl
      });
      toast({ status: 'success', title: 'ロックしました' });
      if (onReloadPreferences) {
        await onReloadPreferences();
      }
      closeLockModal();
    } catch (e) {
      toast({ status: 'error', title: 'ロックに失敗しました', description: e.message });
    } finally {
      setLoadingLock(false);
    }
  };

  const [memoTarget, setMemoTarget] = useState(null); // {item}
  const [isMemoModalOpen, setMemoModalOpen] = useState(false);

  const openMemoModal = async (item) => {
    const user = auth.currentUser;
    if (!user) throw new Error('未ログインです');
    const pref = user_item_preferences?.find(
      p => p.item_type === item.item_type && p.item_id === item.id
    );
    setMemoTarget({
      ...item,
      ...pref,
      item_id: item.id,
      material_files: pref?.material_files || []  // 初期値を空配列に
    });
    setMemoModalOpen(true);
  };
  const closeMemoModal = async () => {
    setMemoTarget(null);
    setMemoModalOpen(false);
    if (onReloadPreferences) {
      await onReloadPreferences();
    }
  };
  const handleMemoSave = async (newMemo, newFiles) => {
    if (!memoTarget) return;
    await onPreferenceChange(memoTarget.item_id, memoTarget.item_type, { memo: newMemo, material_files: newFiles });
    if (onReloadPreferences) {
      await onReloadPreferences();
    }
    closeMemoModal();
  };

  useEffect(() => {
    if (isMemoModalOpen && memoTarget) {
      const pref = user_item_preferences?.find(
        p => p.item_type === memoTarget.item_type && p.item_id === memoTarget.item_id
      );
      setMemoTarget(prev => ({
        ...prev,
        ...pref,
        material_files: pref?.material_files || []  // 初期値を空配列に
      }));
    }
  }, [user_item_preferences, isMemoModalOpen]);

  const [unlockTarget, setUnlockTarget] = useState(null);
  const [isUnlockModalOpen, setUnlockModalOpen] = useState(false);
  const [loadingUnlock, setLoadingUnlock] = useState(false);

  // ロック解除確認モーダル
  const openUnlockModal = (item) => {
    setUnlockTarget(item);
    setUnlockModalOpen(true);
  };
  const closeUnlockModal = () => {
    setUnlockTarget(null);
    setUnlockModalOpen(false);
  };
  const handleUnlock = async () => {
    if (!unlockTarget) return;
    setLoadingUnlock(true);
    try {
      await onPreferenceChange(unlockTarget.id, unlockTarget.item_type, { locked_at: null, signature_url: null });
      toast({ status: 'success', title: 'ロックを解除しました' });
      if (onReloadPreferences) await onReloadPreferences();
      closeUnlockModal();
    } catch (e) {
      toast({ status: 'error', title: 'ロック解除に失敗しました', description: e.message });
    } finally {
      setLoadingUnlock(false);
    }
  };

  const [selectedLocations, setselectedLocations] = useState([]);
  const handleLocationChange = (e, item, index) => {
    setselectedLocations(prev => {
      const newLocations = [...prev];
      newLocations[index] = e.target.value;
      return newLocations;
    });
  };
  const handleLocationDelete = (item, index) => {
    const quantity = selectedLocations.length;
    setselectedLocations(prev => prev.filter((_, i) => i !== index));
    onPreferenceChange(item.id, item.item_type, { quantity: quantity - 1 })
  };
  const handleLocationAdd = (item) => {
    const quantity = selectedLocations.length;
    setselectedLocations(prev => [...prev, '']);
    onPreferenceChange(item.id, item.item_type, { quantity: quantity + 1 })
  };
  return (
    <>
    <Grid
      templateColumns={{
        base: 'repeat(1, 1fr)',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(2, 1fr)',
        lg: 'repeat(2, 1fr)'
      }}
      gap={4}
    >


      {items.map((item, index) => {
        const preference = user_item_preferences?.find(
          p => p.item_type === item.item_type && p.item_id === item.id
        );
        return (
        <GridItem key={item.id}>
          <AnimatedCard delay={index}>
            <Box
            bg={isFixedList ? fixedBg : cardBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius="xl"
            overflow="hidden"
            position="relative"
            boxShadow="sm"
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            _hover={{ 
              transform: 'translateY(-2px)',
              boxShadow: 'lg',
              borderColor: 'gray.300'
            }}
            sx={{
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                bg: item.recommended ? 'brand.500' : 'gray.300',
                opacity: item.recommended ? 1 : 0.3,
                transition: 'opacity 0.2s',
                zIndex: 1
              },
              '&:hover::before': {
                opacity: 1
              }
            }}
          >
            <Box position="relative">
              {getImageUrl(item) ? (
                <Image
                  src={getImageUrl(item)}
                  alt={item.name}
                  w="100%"
                  h="300px"
                  objectFit="contain"
                    onClick={() => onItemSelect(item.id, item.is_user_equipment)}
                  cursor="pointer"
                />
              ) : (
                <Flex
                  w="100%"
                  h="300px"
                  bg="gray.100"
                  align="center"
                  justify="center"
                  color="gray.500"
                    onClick={() => onItemSelect(item.id, item.is_user_equipment)}
                  cursor="pointer"
                >
                  画像なし
                </Flex>
              )}
              
              {/* ランキングバッジ（左上） */}
              {item.rankingBadge && (
                <Box
                  position="absolute"
                  top={2}
                  left={2}
                  w="60px"
                  h="60px"
                  zIndex={2}
                >
                  <Image
                    src={
                      item.rankingBadge === 1 ? rank1Image :
                      item.rankingBadge === 2 ? rank2Image :
                      item.rankingBadge === 3 ? rank3Image : null
                    }
                    alt={`${item.rankingBadge}位`}
                    w="100%"
                    h="100%"
                    objectFit="contain"
                  />
                </Box>
              )}

              <HStack
                position="absolute"
                top={2}
                right={2}
                spacing={2}
              >
                {/* 主要アクション：大きく表示 */}
                <Button
                  size="md"
                  colorScheme="pink"
                  bg="white"
                  onClick={() => onPreferenceChange(item.id, item.item_type, { is_liked: !item.is_liked })}
                  isDisabled={isFixedList ? !!item.locked_at : false}
                  minW="60px"
                  borderRadius="full"
                  boxShadow="sm"
                  _hover={{ 
                    bg: 'pink.50',
                    transform: 'scale(1.05)',
                    boxShadow: 'md'
                  }}
                  _active={{ transform: 'scale(0.95)' }}
                  transition="all 0.15s cubic-bezier(0.4, 0, 0.2, 1)"
                >
                  {item.is_liked ? '❤️' : '🤍'}
                </Button>
                
                {!item.locked_at && (
                  <Tooltip label={item.is_fixed ? "暫定を解除" : "暫定"}>
                                      <Button
                    size="md"
                    colorScheme={item.is_fixed ? "yellow" : "gray"}
                    variant={item.is_fixed ? "solid" : "outline"}
                    bg={item.is_fixed ? "yellow.400" : "white"}
                    onClick={() => onPreferenceChange(item.id, item.item_type, { is_fixed: !item.is_fixed })}
                    minW="60px"
                    borderRadius="full"
                    boxShadow="sm"
                    _hover={{ 
                      bg: item.is_fixed ? 'yellow.500' : 'gray.50',
                      transform: 'scale(1.05)',
                      boxShadow: 'md'
                    }}
                    _active={{ transform: 'scale(0.95)' }}
                    transition="all 0.15s cubic-bezier(0.4, 0, 0.2, 1)"
                  >
                      {item.is_fixed ? <CheckCircleIcon /> : <CheckCircleIcon color="gray.400" />}
                    </Button>
                  </Tooltip>
                )}

                {/* メモ・資料追加 */}
                <Tooltip label="メモ・資料を追加">
                  <Button
                    size="md"
                    colorScheme="gray"
                    variant="outline"
                    bg="white"
                    onClick={() => openMemoModal(item)}
                    minW="60px"
                    borderRadius="full"
                    boxShadow="sm"
                    _hover={{ 
                      bg: 'gray.50',
                      transform: 'scale(1.05)',
                      boxShadow: 'md'
                    }}
                    _active={{ transform: 'scale(0.95)' }}
                    transition="all 0.15s cubic-bezier(0.4, 0, 0.2, 1)"
                  >
                    <EditIcon />
                  </Button>
                </Tooltip>

                {/* ロック */}
                {item.is_fixed && !item.locked_at && !isAdmin && isFunctionEnabled('lock') && (
                  <Tooltip label="ロック">
                    <Button
                      size="md"
                      colorScheme="yellow"
                      variant="outline"
                      bg="white"
                      onClick={() => {
                        setLockTarget(item);
                        setSigModalOpen(true);
                      }}
                      minW="60px"
                      borderRadius="full"
                      boxShadow="sm"
                      _hover={{ 
                        bg: 'yellow.50',
                        transform: 'scale(1.05)',
                        boxShadow: 'md'
                      }}
                      _active={{ transform: 'scale(0.95)' }}
                      transition="all 0.15s cubic-bezier(0.4, 0, 0.2, 1)"
                    >
                      <LockIcon />
                    </Button>
                  </Tooltip>
                )}
              </HStack>
            </Box>

            <VStack p={4} align="stretch" spacing={2}>
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color={subTextColor}>
                  {item.manufacturer}
                </Text>
                  {item.is_user_equipment ? (
                    <Badge 
                      colorScheme="black" 
                      bg="gray.800" 
                      color="white"
                      px={3}
                      py={1}
                      fontSize="xs"
                      fontWeight="bold"
                      borderRadius="full"
                      boxShadow="sm"
                    >
                      ユーザー登録
                    </Badge>
                  ) : item.recommended && (
                    <Badge 
                      colorScheme="brand" 
                      bg="brand.500" 
                      color="white"
                      px={3}
                      py={1}
                      fontSize="xs"
                      fontWeight="bold"
                      borderRadius="full"
                      boxShadow="sm"
                    >
                      おすすめ
                    </Badge>
                  )}
              </Flex>
              <Text
                fontSize="md"
                fontWeight="bold"
                color={textColor}
                noOfLines={2}
                  onClick={() => onItemSelect(item.id, item.is_user_equipment)}
                cursor="pointer"
              >
                {item.name}
              </Text>
              <Box fontSize="lg" fontWeight="bold" color="black">
                {formatPrice(item.price)}
              </Box>
              <Text fontSize="sm" color={subTextColor} noOfLines={2}>
                {item.features}
              </Text>

              {/* 外部リンクボタン */}
              {item.external_link_url && (
                <Button
                  size="md"
                  bg="black"
                  color="white"
                  _hover={{ 
                    bg: 'gray.700',
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg'
                  }}
                  _active={{ 
                    bg: 'gray.800',
                    transform: 'translateY(0)'
                  }}
                  as="a"
                  href={item.external_link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  mt={2}
                  borderRadius="lg"
                  boxShadow="md"
                  transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                >
                  {item.external_link_name || '詳細を見る'}
                </Button>
              )}

              { !isFixedList && config.fields?.map(field => {
                const value = item[field.name];
                if (value === undefined || value === null || 
                    field.name === 'price' || field.name === 'features' || 
                    field.name === 'recommended' || field.name === 'manufacturer' || 
                    field.name === 'name' || field.name === 'images' ||
                    (item.is_user_equipment && field.name === 'user_id') ||
                    field.showInCard === false) return null;
                // floorplanのm2のみ単位切替
                if (config.key === 'floorplan' && field.unit === 'm2') {
                  return (
                    <Text key={field.name} fontSize="sm" color={subTextColor}>
                      {field.label}: {isTsubo ? (value ? (Number(value) / 3.305785).toFixed(2) + '坪' : '') : value + 'm²'}
                    </Text>
                  );
                }
                return (
                  <Text key={field.name} fontSize="sm" color={subTextColor}>
                    {field.label}: {typeof value === 'boolean' ? (value ? 'あり' : 'なし') : value}{field.unit || ''}
                  </Text>
                );
              })}
            </VStack>

            {isFixedList && (
                <Flex align="center" mt={2} mb={2} gap={2}>
                  <Text fontSize="sm" color="gray.700" fontWeight="bold">数量:</Text>
                  <Button
                    size="sm"
                    onClick={() => onPreferenceChange(item.id, item.item_type, { quantity: Math.max(1, (item.quantity || 1) - 1) })}
                    isDisabled={isFunctionEnabled('lock') ? !!item.locked_at || (item.quantity || 1) <= 1 : (item.quantity || 1) <= 1}
                    aria-label="数量を減らす"
                    borderRadius="full"
                    bg="gray.100"
                    _hover={{ 
                      bg: 'gray.200',
                      transform: 'scale(1.1)'
                    }}
                    _active={{ transform: 'scale(0.95)' }}
                    transition="all 0.15s cubic-bezier(0.4, 0, 0.2, 1)"
                  >
                    -
                  </Button>
                  <Text fontSize="md" w="32px" textAlign="center" fontWeight="bold">{item.quantity || 1}</Text>
                  <Button
                    size="sm"
                    onClick={() => onPreferenceChange(item.id, item.item_type, { quantity: (item.quantity || 1) + 1 })}
                    isDisabled={isFunctionEnabled('lock') ? !!item.locked_at : false}
                    aria-label="数量を増やす"
                    borderRadius="full"
                    bg="gray.100"
                    _hover={{ 
                      bg: 'gray.200',
                      transform: 'scale(1.1)'
                    }}
                    _active={{ transform: 'scale(0.95)' }}
                    transition="all 0.15s cubic-bezier(0.4, 0, 0.2, 1)"
                  >
                    +
                  </Button>
                </Flex>
              )}
            {isFixedList && item.locked_at && isFunctionEnabled('lock') && (
                <Box p={2} mt={2} bg="gray.100" borderRadius="md" borderWidth="1px" borderColor="gray.100">
                  <Text fontSize="sm" color="gray.700" fontWeight="bold">
                    ロック日時: {item.locked_at ? dayjs(item.locked_at).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm:ss') : ''}
                  </Text>
                  {item.signature_url && (
                    <Image 
                      src={item.signature_url} 
                      alt="サイン" 
                      boxSize="120px" 
                      mt={1} 
                      borderRadius="md" 
                      bg="white" 
                      cursor="pointer"
                      onClick={() => {
                        setSelectedImage(item.signature_url);
                        setSelectedImageAlt("サイン");
                        setIsImageModalOpen(true);
                      }}
                      title="クリックして拡大表示"
                    />
                  )}
                </Box>
              )}


            </Box>
          </AnimatedCard>
        </GridItem>
        );
      })}
    </Grid>

    <SignaturePadModal
      isOpen={isSigModalOpen && isFunctionEnabled('lock')}
      onClose={closeLockModal}
      onSave={handleLock}
    />



    <MemoModal
      isOpen={isMemoModalOpen}
      onClose={closeMemoModal}
      memo={memoTarget?.memo}
      onSave={handleMemoSave}
      isLocked={isFunctionEnabled('lock') ? !!memoTarget?.locked_at : false}
      preferenceId={memoTarget?.id}
      materialFiles={memoTarget?.material_files}
      itemType={memoTarget?.item_type}
      onUploadMaterial={async (preferenceId, file) => {
        const user = auth.currentUser;
        if (!user) throw new Error('未ログインです');
        const data = await uploadUserItemPreferenceMaterial(
          memoTarget.item_type,
          memoTarget.item_id,
          file,
          user,
          targetUserId
        );
        setMemoTarget(prev => ({
          ...prev,
          material_files: data.material_files
        }));
      }}
      onReloadMaterialFiles={onReloadMaterialFiles}
      onPreferenceChange={onPreferenceChange}
      itemId={memoTarget?.item_id}
      onImageClick={(url, alt) => {
        setSelectedImage(url);
        setSelectedImageAlt(alt);
        setIsImageModalOpen(true);
      }}
    />

    <Modal isOpen={isUnlockModalOpen && isFunctionEnabled('lock')} onClose={closeUnlockModal} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>ロック解除確認</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>このアイテムのロックを解除しますか？</Text>
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeUnlockModal} mr={3}>キャンセル</Button>
          <Button bg="red.600" color="white" onClick={handleUnlock} isLoading={loadingUnlock}>OK</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* 拡大画像モーダル */}
    <Modal isOpen={isImageModalOpen} onClose={() => setIsImageModalOpen(false)} size="full" isCentered>
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg="transparent" boxShadow="none" w="100vw" h="100vh" m={0}>
        <ModalCloseButton 
          color="white" 
          bg="blackAlpha.600" 
          _hover={{ bg: "blackAlpha.800" }}
          size={{ base: "md", md: "lg" }}
          zIndex={20}
          position="fixed"
          top={{ base: 2, md: 4 }}
          right={{ base: 2, md: 4 }}
        />
        <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
          {selectedImage && (
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={4}
              centerOnInit={true}
              limitToBounds={false}
              smooth={true}
              wheel={{
                step: 0.1,
                wheelEnabled: true,
                touchPadEnabled: true,
              }}
              pinch={{
                step: 1,
                disabled: false,
              }}
              doubleClick={{
                step: 2,
                mode: "toggle",
              }}
              onTransformed={(state) => {
                // ズーム状態を追跡
              }}
            >
              {({ zoomIn, zoomOut, resetTransform, state }) => (
                <TransformComponent
                  wrapperStyle={{
                    width: "100%",
                    height: "100vh",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    touchAction: "none",
                  }}
                  contentStyle={{
                    width: "100%",
                    height: "100%",
                    touchAction: "none",
                  }}
                >
                  <Box position="relative" w="100%" h="100%">
                    <Image
                      src={selectedImage}
                      alt={selectedImageAlt}
                      w="100%"
                      h="100vh"
                      objectFit="contain"
                      draggable={false}
                      userSelect="none"
                      style={{
                        touchAction: "none",
                        WebkitTouchCallout: "none",
                        WebkitUserSelect: "none",
                        KhtmlUserSelect: "none",
                        MozUserSelect: "none",
                        msUserSelect: "none",
                        userSelect: "none",
                      }}
                    />
                    {/* ズームインジケーター */}
                    {state && state.scale !== 1 && (
                      <Box
                        position="absolute"
                        top={4}
                        left="50%"
                        transform="translateX(-50%)"
                        bg="blackAlpha.600"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="md"
                        fontSize="sm"
                        zIndex={10}
                      >
                        {Math.round(state.scale * 100)}%
                      </Box>
                    )}
                    {/* ズームコントロール */}
                    <HStack
                      position="absolute"
                      bottom={4}
                      left="50%"
                      transform="translateX(-50%)"
                      spacing={2}
                      zIndex={10}
                    >
                      <IconButton
                        icon={<Text fontSize="sm">+</Text>}
                        onClick={() => zoomIn()}
                        bg="blackAlpha.600"
                        color="white"
                        _hover={{ bg: "blackAlpha.800" }}
                        size="sm"
                        aria-label="ズームイン"
                      />
                      <IconButton
                        icon={<Text fontSize="sm">-</Text>}
                        onClick={() => zoomOut()}
                        bg="blackAlpha.600"
                        color="white"
                        _hover={{ bg: "blackAlpha.800" }}
                        size="sm"
                        aria-label="ズームアウト"
                      />
                      <IconButton
                        icon={<Text fontSize="sm">⟲</Text>}
                        onClick={() => resetTransform()}
                        bg="blackAlpha.600"
                        color="white"
                        _hover={{ bg: "blackAlpha.800" }}
                        size="sm"
                        aria-label="リセット"
                      />
                    </HStack>
                  </Box>
                </TransformComponent>
              )}
            </TransformWrapper>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
    </>
  );
}

export { UserEquipmentModal }; 